import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { LlmUsage } from "@studio-features/meta-crew/llm-usage";
import { AiKeySettings, type AiKeySummaryActions } from "@studio-features/meta-crew/llm-keys";
import type { AiCall, AiFilters, AiKey, AiKeysActions, AiKeysView } from "@studio-features/meta-crew/llm-data";
import { initialUsageQuery, type UsageExploreOptions, type UsageExploreQuery, type UsageGroup } from "@studio-features/meta-crew/usage-explorer-data";
import { api, errorMessage } from "../lib/api";
import { can } from "../lib/access";
import { kstDay, llmInvalidate, readiness, type LlmKey, useAiKeySettings, useAiUsage, useUsageExplore } from "../lib/llm-api";
import { useAdminMachines, useCustomers } from "../lib/queries";
import type { Me } from "../lib/types";
import { Loading, PanelError } from "../console/ui";

/**
 * 시스템 설정 › AI 키 (요청서 22절, 시안 fd20351 AiKeySettings). docs/meta-crew-llm-relay-design.md.
 * 화면(요약 KPI → 키별 사용 모델 → 기본 모델 → 단가 보기)은 시안 부품, 여기서는 서버 값을 view 로 넘기고 API 호출을 actions 로 넣는다.
 * 모델 목록·준비 상태·기본 모델 유효성은 서버 판정을 그대로 쓴다 (화면에서 다시 계산하지 않음).
 */
export function LlmKeysScreen({ me, onUsage }: { me: Me; onUsage?: () => void }) {
  const { keys, settings } = useAiKeySettings();
  const qc = useQueryClient();
  if (keys.error) return <PanelError error={errorMessage(keys.error)} onRetry={() => void keys.refetch()} />;
  if (!keys.data || !settings.data) return <Loading />;
  const { keys: list, models, relay } = keys.data;
  const view: AiKeysView = {
    keys: list.map(toAiKey),
    models,
    relay: readiness(relay),
    settings: {
      enabled: settings.data.enabled,
      model: models.default,
      effort: settings.data.effort,
      allowedModels: models.allowed,
      prices: settings.data.prices,
      priceSource: settings.data.priceSource,
    },
  };
  const refresh = async () => {
    await Promise.all(llmInvalidate.map((k) => qc.invalidateQueries({ queryKey: k })));
  };
  // 쓰기 뒤에는 늘 다시 읽는다 (실패해도 서버 상태가 바뀌었을 수 있음)
  const after = async <T,>(p: Promise<T>): Promise<T> => {
    try {
      return await p;
    } finally {
      await refresh();
    }
  };
  const actions: AiKeysActions = {
    create: async (input) => {
      const r = await after(api.post<{ key: LlmKey; note?: string }>("/admin/llm/keys", input));
      return { key: toAiKey(r.key), note: r.note };
    },
    patch: async (id, input) => {
      await after(api.patch<LlmKey>(`/admin/llm/keys/${id}`, input));
    },
    test: async (id) => {
      const r = await after(api.post<{ ok: boolean; key: LlmKey; error?: string; note?: string }>(`/admin/llm/keys/${id}/test`));
      return { ok: r.ok, key: toAiKey(r.key), error: r.error, note: r.note };
    },
    remove: async (id) => {
      await after(api.del(`/admin/llm/keys/${id}`));
    },
    saveDefault: async (input) => {
      await after(api.put("/admin/llm/default", input));
    },
    setRelayEnabled: async (enabled) => {
      await after(api.put("/admin/settings/llm_relay_enabled", { value: enabled }));
    },
    syncPrices: async () => {
      const r = await after(api.post<{ ok: boolean; error?: string }>("/admin/llm/prices/sync"));
      if (!r.ok) throw new Error(r.error ?? "단가를 가져오지 못했습니다.");
    },
    refresh,
  };
  // 요청서 25절 (시안 ba7cb9b summaryActions): 요약 카드 → 같은 화면의 키 목록·기본 모델, 이번 달 호출 → 모니터링 › LLM 사용량
  const scrollTo = (id: string) => () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const summaryActions: AiKeySummaryActions = {
    keys: { onSelect: scrollTo("ai-key-list"), actionLabel: "등록 키 — 키 목록으로 이동" },
    models: { onSelect: scrollTo("ai-key-list"), actionLabel: "사용 가능 모델 — 키별 사용 모델 보기" },
    "default-model": { onSelect: scrollTo("ai-default-model"), actionLabel: "기본 모델 — 기본 모델 설정으로 이동" },
    ...(onUsage ? { usage: { onSelect: onUsage, actionLabel: "이번 달 호출 — LLM 사용량에서 보기" } } : {}),
  };
  return <AiKeySettings view={view} actions={actions} canManage={can(me, "LLM_KEY_MANAGE")} summaryActions={summaryActions} />;
}

/** 서버 키 행 → 시안 AiKey (같은 칸, createdAt 만 뺀다). */
const toAiKey = (k: LlmKey): AiKey => ({
  id: k.id,
  label: k.label,
  hint: k.hint,
  status: k.status,
  priority: k.priority,
  monthlyBudgetUsd: k.monthlyBudgetUsd,
  month: k.month,
  lastUsedAt: k.lastUsedAt,
  lastCheckedAt: k.lastCheckedAt,
  lastError: k.lastError,
  models: k.models,
  modelsSource: k.modelsSource,
  enabledModels: k.enabledModels,
});

export function LlmUsageScreen({ onOpenRun }: { onOpenRun: (runId: string, customerId: string) => void }) {
  const [filters, setFilters] = useState<AiFilters>(() => ({ from: kstDay(6), to: kstDay(0), customerId: "all", machineId: "all", model: "all", mode: "all", status: "all" }));
  const [limit, setLimit] = useState(50);
  const customers = useCustomers("", true);
  const machines = useAdminMachines();
  const lists = useMemo(
    () => ({
      customers: (customers.data?.items ?? []).map((c) => ({ id: c.id, label: c.name })),
      machines: (machines.data?.items ?? []).map((m) => ({ id: m.id, label: m.label })),
    }),
    [customers.data, machines.data],
  );
  const q = useAiUsage(filters, limit, lists);
  // 사용량 탐색 (요청서 28절): 요약·묶음 보기를 대신한다. 호출 이력은 위 q 그대로.
  const [query, setQuery] = useState<UsageExploreQuery>(() => initialUsageQuery());
  const explore = useUsageExplore(query);
  const { keys } = useAiKeySettings();
  const options = useMemo<UsageExploreOptions>(() => {
    const customerOptions = (customers.data?.items ?? []).map((c) => ({ value: c.id, label: c.name }));
    return {
      customerId: customerOptions,
      targetCustomerId: customerOptions,
      machineId: (machines.data?.items ?? []).map((m) => ({ value: m.id, label: m.label })),
      keyId: (keys.data?.keys ?? []).map((k) => ({ value: k.id, label: `${k.label} · ${k.hint}` })),
      model: (q.data?.models ?? []).map((m) => ({ value: m, label: m })),
      // 요청서 30절: 공급자(API 키 / GPT 플랜) 차원·필터. 서버 llmCall.provider
      provider: [
        { value: "openai", label: "API 키" },
        { value: "codex", label: "GPT 플랜" },
      ],
      mode: [
        { value: "local", label: "로컬 세션" },
        { value: "relay", label: "API 키" },
        { value: "refine", label: "원고 정형화" },
      ],
      status: [
        { value: "ok", label: "성공" },
        { value: "error", label: "실패" },
        { value: "cancelled", label: "취소" },
      ],
    };
  }, [customers.data, machines.data, keys.data, q.data?.models]);
  // 작업·시도 행 → 실행 상세. 시도(run) 키가 있으면 그 실행, 작업(job)만 있으면 그 작업의 가장 최근 시도.
  const openGroup = (g: UsageGroup) => {
    const part = (dim: string) => g.parts.find((p) => p.dim === dim)?.key ?? null;
    const runId = part("run");
    const jobId = part("job");
    const found = runId
      ? api.get<{ id: string; customerId: string | null }>(`/admin/runs/${runId}`)
      : jobId
        ? api.page<{ id: string; customerId: string | null }>(`/admin/runs?jobId=${jobId}&limit=1`).then((r) => r.items[0] ?? null)
        : Promise.resolve(null);
    void found.then((r) => r?.customerId && onOpenRun(r.id, r.customerId)).catch(() => undefined);
  };
  if (!q.data && q.isLoading) return <Loading />;
  return (
    <LlmUsage
      explorer={{
        query,
        view: explore.data,
        options,
        providerAvailable: true,
        loading: explore.isFetching,
        error: explore.error ? errorMessage(explore.error) : undefined,
        onQueryChange: setQuery,
        onRetry: () => void explore.refetch(),
        onOpenRun: openGroup,
      }}
      view={q.data}
      filters={filters}
      onFiltersChange={(f) => {
        setFilters(f);
        setLimit(50);
      }}
      onLoadMore={() => setLimit((l) => l + 50)}
      loading={q.isFetching}
      error={q.error ? errorMessage(q.error) : undefined}
      onRetry={() => void q.refetch()}
      onOpenRun={(c: AiCall) => c.runId && onOpenRun(c.runId, c.customerId)}
    />
  );
}
